import { useAppSelector } from 'hooks/useAppSelector';
import { useTranslation } from 'hooks/useTranslation';
import type { WorkspaceType } from 'state/workspaces/model';

import { DocumentLink } from '../design-system/DocumentLink';
import { LanguageLink } from '../design-system/LanguageLink';
import {
  getProductGovernanceDocumentError,
  getProductGovernanceDocuments,
  getProductGovernanceDocumentTimeout,
  hasProductGovernanceError,
} from '../selectors';
import { useOnDownloadDocument } from './useOnDownloadDocument';

type Props = {
  tabId: string;
  workspaceType: WorkspaceType;
  className?: string;
};

export function ProductGovernanceDocuments({ tabId, workspaceType, className }: Props) {
  const label = useTranslation('form.buttons.ProductGovernanceDocuments');
  const documents = useAppSelector(state => getProductGovernanceDocuments(state, workspaceType, tabId));
  const rejections = useAppSelector(state => getProductGovernanceDocumentError(state, workspaceType, tabId));
  const hasError = useAppSelector(state => hasProductGovernanceError(state, workspaceType, tabId));
  const hasTimeout = useAppSelector(state => getProductGovernanceDocumentTimeout(state, workspaceType, tabId));
  const { onDownload, isDownloading, onShowError } = useOnDownloadDocument({
    errorLabelKey: 'errorDetails.pgDocument.standardMessage',
    event: 'Download target market language',
  });

  if (hasTimeout || hasError) {
    return (
      <DocumentLink label={label} className={className}>
        <LanguageLink
          langCode={null}
          status="error"
          severity={hasTimeout ? 'warning' : 'danger'}
          onShowError={hasTimeout ? () => onShowError([]) : () => onShowError(rejections ?? [])}
        />
      </DocumentLink>
    );
  }

  if (documents.length === 0) {
    return null;
  }

  return (
    <DocumentLink label={label} className={className}>
      {documents.map(doc => {
        if (doc.documentToken !== undefined && isDownloading) {
          return <LanguageLink key={doc.language} langCode={doc.language} status="pending" />;
        }
        return (
          <LanguageLink
            key={doc.language}
            langCode={doc.language}
            status="success"
            onDownload={() => onDownload(doc)}
          />
        );
      })}
    </DocumentLink>
  );
}
